import { NovelRankingType } from '@app/_proto/Protos/novel_rankings';
import type { NovelItem } from '@app/_proto/Protos/novels';
import { useMediaQuery } from '@app/domains/common/hooks/useMediaQuery';
import { HomeCollectionSlots } from '@app/domains/home/analytics/posthog/constants';
import HomeCollection from '@app/domains/home/components/HomeCollection';
import HomePopularContent from '@app/domains/home/components/HomePopularContent';
import HomeSwiper from '@app/domains/home/components/HomeSwiper';
import {
    HOME_COLLECTION_TITLE_FONT_STYLE,
    HOME_SWIPER_SLIDE_SIZE_PER_VIEW_STYLE,
    HOME_SWIPER_WITH_TOP_SHADOW_LAYOUT_STYLE,
} from '@app/domains/home/styles';
import useNovelRankings from '@app/domains/novel/hooks/useNovelRankings';
import { breakpoints } from '@app/utils/breakpoints';
import { useInView } from 'react-intersection-observer';
import { twMerge } from 'tailwind-merge';

export default function HomePopularNovels() {
    const { ref, inView } = useInView({
        triggerOnce: true,
    });

    const { data: contents = Array<undefined>(10).fill(undefined), isSuccess } = useNovelRankings({
        type: NovelRankingType.Weekly,
        enabled: inView,
    });

    const isLoading = !isSuccess;

    const slidesPerView = useHomePopularSlidesPerView();
    const isDownLg = useMediaQuery(breakpoints.downLg);

    return (
        <div ref={ref}>
            <HomeCollection data-posthog-params={JSON.stringify({ collectionSlot: HomeCollectionSlots.Popular })}>
                <HomeCollection.Title
                    title="Popular This Week"
                    classes={{ heading: HOME_COLLECTION_TITLE_FONT_STYLE }}
                />
                <div className="pt-8">
                    <HomeSwiper
                        slidesPerView={slidesPerView}
                        slidesPerGroup={isDownLg ? 1 : 5}
                        className={HOME_SWIPER_WITH_TOP_SHADOW_LAYOUT_STYLE}
                        isLoading={isLoading}
                    >
                        {contents?.map((content: NovelItem | undefined, rank) => (
                            <HomeSwiper.Slide
                                key={rank}
                                className={twMerge(
                                    HOME_SWIPER_SLIDE_SIZE_PER_VIEW_STYLE,
                                    'lg:!w-[calc((100%+16px)/5-16px)]'
                                )}
                                data-posthog-params={JSON.stringify({ itemSlot: rank })}
                            >
                                <HomePopularContent content={content} rank={rank} isLoading={isLoading} />
                            </HomeSwiper.Slide>
                        ))}
                    </HomeSwiper>
                </div>
            </HomeCollection>
        </div>
    );
}

export const useHomePopularSlidesPerView = () => {
    const isDownXs2 = useMediaQuery(breakpoints.downXs2);
    const isDownSm2 = useMediaQuery(breakpoints.downSm2);
    const isDownLg = useMediaQuery(breakpoints.downLg);
    const isUpLg = useMediaQuery(breakpoints.upLg);

    if (isDownXs2) return 3;
    if (isDownSm2) return 'auto';
    if (isDownLg) return 5;
    if (isUpLg) return 5;
    return 'auto';
};
